import { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { useMap } from 'react-map-gl';
import { throttle } from 'lodash-es';
import type { MapMouseEvent } from 'mapbox-gl';
import { mapClickOwnersAtom, measureModeAtom } from 'components/map';
import { useLatest } from 'lib/hooks/use-latest';
import { SURVEY_FILL_LAYER } from './survey-areas-layer';

/**
 * The claim a host makes while Create New Area has its map. The pick is part of
 * that work, so this claim doesn't stand it down; any other one does.
 */
export const CREATE_AREA_CLICK_OWNER = 'create-area';

/** Cursor feedback stays imperceptible at this rate, and costs one query. */
const CURSOR_THROTTLE_MS = 50;

/**
 * The shared map's own development-area fill layers, reused rather than adding a
 * second source: they are already rendered, already carry `development_area_id`,
 * and are fills, so a click anywhere inside a unit hits one.
 */
const UNIT_FILL_LAYERS = [
  'development-area-horizontal-fill',
  'development-area-vertical-fill',
];

/**
 * One survey area under the click, as the tile describes it. Everything past the
 * id and name tells overlapping tracts apart: the Texas-only survey fields, and
 * the acreage that settles which of two records for one tract to take.
 */
export interface SurveyAreaCandidate {
  id: number;
  name: string;
  type?: string;
  surveyName?: string;
  block?: string;
  section?: string;
  abstract?: string;
  acreage?: number;
}

interface MapPickControlProps {
  /** Fires with the clicked unit's id. Null while unit picking is off. */
  onPickUnit?: ((developmentAreaId: number) => void) | null;
  /**
   * Fires with *every* survey area under the click, not just one: sections, their
   * tracts and other survey systems all overlap in the tile, so which was meant is
   * not the map's decision. Null while section picking is off.
   */
  onPickSurveyArea?: ((candidates: SurveyAreaCandidate[]) => void) | null;
}

/**
 * Headless child of the map that turns a click into a unit pick or a survey-area
 * pick. One listener rather than one per layer, since a click can land on several
 * at once — and the unit layers win, being the more specific thing to have hit.
 *
 * @param {MapPickControlProps} props - The pick callbacks.
 * @return {null} Always renders nothing.
 */
export const MapPickControl = ({
  onPickUnit,
  onPickSurveyArea,
}: MapPickControlProps) => {
  const { current: map } = useMap();
  // Measuring or dropping coordinate pins owns the click, and sets its own cursor.
  // Create New Area's own claim is the one that leaves the pick listening.
  const measureMode = useAtomValue(measureModeAtom);
  const owners = useAtomValue(mapClickOwnersAtom);
  const clicksTaken =
    measureMode || owners.some((owner) => owner !== CREATE_AREA_CLICK_OWNER);
  // Read at click time, so a new callback never re-wires the listeners.
  const picks = useLatest({ onPickUnit, onPickSurveyArea });

  useEffect(() => {
    if (!map || clicksTaken) {
      return undefined;
    }

    // Only layers actually present can be queried — the vertical-units layer is
    // off in the default map config, the survey layer only mounts for the survey
    // method, and mapbox throws on a layer id that doesn't exist.
    const pickable = () => {
      const { onPickUnit, onPickSurveyArea } = picks.current;
      const unit = onPickUnit
        ? UNIT_FILL_LAYERS.filter((id) => map.getLayer(id))
        : [];
      const survey =
        onPickSurveyArea && map.getLayer(SURVEY_FILL_LAYER)
          ? [SURVEY_FILL_LAYER]
          : [];
      return { unit, survey, all: [...unit, ...survey] };
    };

    const handleClick = (event: MapMouseEvent) => {
      const { onPickUnit, onPickSurveyArea } = picks.current;
      const layers = pickable();
      if (layers.all.length === 0) {
        return;
      }

      const features = map.queryRenderedFeatures(event.point, {
        layers: layers.all,
      });

      const unit = features.find((feature) =>
        layers.unit.includes(feature.layer?.id ?? '')
      );
      if (unit && onPickUnit) {
        const id = unit.properties?.development_area_id;
        if (id !== undefined && id !== null) {
          onPickUnit(Number(id));
        }
        return;
      }

      if (!onPickSurveyArea) {
        return;
      }

      // Deduped: one survey area can be split across the tiles the click point
      // sits on the seam of, and would otherwise be counted twice.
      const byId = new Map<number, SurveyAreaCandidate>();
      features
        .filter((feature) => feature.layer?.id === SURVEY_FILL_LAYER)
        .forEach((feature) => {
          const props = feature.properties ?? {};
          const id = props.reference_location_id;
          if (id !== undefined && id !== null) {
            byId.set(Number(id), {
              id: Number(id),
              name: String(props.name ?? ''),
              // A null column is left out of the tile altogether, so these stay
              // undefined rather than arriving as null.
              type: props.type ?? undefined,
              surveyName: props.survey_name ?? undefined,
              block: props.block ?? undefined,
              section: props.section ?? undefined,
              abstract: props.abstract ?? undefined,
              acreage:
                props.calculated_acreage === undefined ||
                props.calculated_acreage === null
                  ? undefined
                  : Number(props.calculated_acreage),
            });
          }
        });

      if (byId.size > 0) {
        onPickSurveyArea([...byId.values()]);
      }
    };

    // The cursor is the only signal that a click means something, so it tracks
    // exactly what the click handler would find. Throttled, because a rendered-
    // feature query on every pointer move is the expensive way to ask.
    const handleMove = throttle((event: MapMouseEvent) => {
      const layers = pickable();
      const over =
        layers.all.length > 0 &&
        map.queryRenderedFeatures(event.point, { layers: layers.all }).length >
          0;
      const cursor = over ? 'pointer' : '';
      // The map can be mid-teardown (modal close) when a throttled move lands —
      // getCanvas() is undefined on a removed map, so guard before touching style.
      const canvas = map.getCanvas?.();
      if (canvas && canvas.style.cursor !== cursor) {
        canvas.style.cursor = cursor;
      }
    }, CURSOR_THROTTLE_MS);

    // Wired at once, whatever the style is doing: waiting on `style.load` missed it
    // whenever the control mounted on a map that had already loaded, and
    // `isStyleLoaded()` reads false while any source loads, so picking died at
    // random. A click before the style is up finds none of the pick layers in
    // `pickable()` and does nothing.
    map.on('click', handleClick);
    map.on('mousemove', handleMove);

    return () => {
      map.off('click', handleClick);
      map.off('mousemove', handleMove);
      handleMove.cancel();
      // On modal close the underlying map is removed before this cleanup runs,
      // so getCanvas() returns undefined — guard the reset.
      const canvas = map.getCanvas?.();
      if (canvas) {
        canvas.style.cursor = '';
      }
    };
  }, [map, clicksTaken, picks]);

  // Arming or disarming a pick leaves no pointer cursor behind from the old one;
  // the next move sets it again. Keyed on whether each pick is armed, so a host
  // passing a new callback doesn't clear the cursor under the pointer.
  const unitArmed = !!onPickUnit;
  const surveyArmed = !!onPickSurveyArea;
  useEffect(
    () => () => {
      const canvas = map?.getCanvas?.();
      if (canvas) {
        canvas.style.cursor = '';
      }
    },
    [map, unitArmed, surveyArmed]
  );

  return null;
};

export default MapPickControl;
